/**
 * Global Modal State Context
 * 
 * Provides persistent modal state that survives component re-renders,
 * React Query refetches, and cache invalidation events.
 * 
 * Solution for modal closing issues when switching browser tabs.
 */

import React, { createContext, useContext, useCallback, useRef } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export interface ModalState {
  id: string;
  isOpen: boolean;
  data?: any;
  timestamp: number;
}

interface ModalContextValue {
  // Modal state management
  openModal: (id: string, data?: any) => void;
  closeModal: (id: string) => void;
  isModalOpen: (id: string) => boolean;
  getModalData: (id: string) => any;
  
  // Utility functions
  closeAllModals: () => void;
  getOpenModals: () => string[];
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

interface ModalProviderProps {
  children: React.ReactNode;
  persistToLocalStorage?: boolean;
  storageKey?: string;
}

export function ModalProvider({ 
  children, 
  persistToLocalStorage = true,
  storageKey = 'terrarium_modal_state'
}: ModalProviderProps) {
  
  // Use localStorage for persistence across tab switches and cache invalidations
  const [modalStates, setModalStates] = useLocalStorage<Record<string, ModalState>>(
    storageKey,
    {},
    {
      enabled: persistToLocalStorage,
      // Clean up old modal states (older than 1 hour)
      serialize: (value) => {
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        const cleanedValue = Object.fromEntries(
          Object.entries(value).filter(([_, modal]) => modal.timestamp > oneHourAgo)
        );
        return JSON.stringify(cleanedValue);
      }
    }
  );

  // Keep track of active modals for cleanup
  const activeModalsRef = useRef<Set<string>>(new Set());

  const openModal = useCallback((id: string, data?: any) => {
    setModalStates(prev => ({
      ...prev,
      [id]: {
        id,
        isOpen: true,
        data,
        timestamp: Date.now()
      }
    }));
    activeModalsRef.current.add(id);
  }, [setModalStates]);

  const closeModal = useCallback((id: string) => {
    setModalStates(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isOpen: false,
        timestamp: Date.now()
      }
    }));
    activeModalsRef.current.delete(id);
  }, [setModalStates]);

  const isModalOpen = useCallback((id: string) => {
    return modalStates[id]?.isOpen ?? false;
  }, [modalStates]);

  const getModalData = useCallback((id: string) => {
    return modalStates[id]?.data;
  }, [modalStates]);

  const closeAllModals = useCallback(() => {
    const updatedStates = Object.fromEntries(
      Object.entries(modalStates).map(([id, state]) => [
        id,
        { ...state, isOpen: false, timestamp: Date.now() }
      ])
    );
    setModalStates(updatedStates);
    activeModalsRef.current.clear();
  }, [modalStates, setModalStates]);

  const getOpenModals = useCallback(() => {
    return Object.entries(modalStates)
      .filter(([_, state]) => state.isOpen)
      .map(([id]) => id);
  }, [modalStates]);

  const contextValue: ModalContextValue = {
    openModal,
    closeModal,
    isModalOpen,
    getModalData,
    closeAllModals,
    getOpenModals,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
}

/**
 * Hook for managing individual modal state with persistence
 */
export function usePersistedModal(modalId: string) {
  const { openModal, closeModal, isModalOpen, getModalData } = useModalContext();

  const open = useCallback((data?: any) => {
    openModal(modalId, data);
  }, [openModal, modalId]);

  const close = useCallback(() => {
    closeModal(modalId);
  }, [closeModal, modalId]);

  return {
    isOpen: isModalOpen(modalId),
    open,
    close,
    data: getModalData(modalId),
  };
}

/**
 * Modal IDs - centralized constants to avoid typos
 */
export const MODAL_IDS = {
  POST_JOB_OVERLAY: 'post-job-overlay',
  CREATE_EVENT: 'create-event',
  EDIT_EVENT: 'edit-event',
  EMPLOYER_CUSTOMIZATION: 'employer-customization',
  MEMBER_PROFILE: 'member-profile',
  // Add more modal IDs as needed
} as const;